/**
 * PlanCard Component
 *
 * Displays a single subscription plan with pricing, features, and upgrade button.
 * Used in billing dialogs and pricing pages for consistent plan presentation.
 */

import { Check, CheckCircle2, Loader2, Star } from 'lucide-react';
import type { BillingInterval, PlanCardData } from '@loader/shared-types';
import { buildFeatureList } from '@loader/shared-types';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@loader/ui';
import { Button } from '@loader/ui';
import { Separator } from '@loader/ui';

import { formatPrice } from '@/lib/formatters';
import { cn } from '@/lib/utils';

export type { PlanCardData };

export interface PlanCardProps {
  /** Plan data to display */
  plan: PlanCardData;
  /** Billing interval for price display */
  billingInterval: BillingInterval;
  /** Whether this plan is recommended */
  isRecommended?: boolean;
  /** Whether the upgrade button is disabled */
  disabled?: boolean;
  /** Loading state for upgrade button */
  loading?: boolean;
  /** Callback when upgrade button is clicked */
  onUpgrade: (planId: string) => void;
  /** Custom button text (defaults to "Upgrade to {planName}") */
  buttonText?: string;
  /** Custom className for the card */
  className?: string;
  /** Whether to show trial days (defaults to true) */
  showTrial?: boolean;
  /** Whether this is the user's current plan */
  isCurrentPlan?: boolean;
  /** Whether the user is currently on a trial */
  isTrial?: boolean;
  /** Current plan's price in cents (for upgrade/downgrade text) */
  currentPlanPrice?: number | null;
}

/**
 * PlanCard - Displays a subscription plan with features and pricing
 *
 * @example
 * ```tsx
 * <PlanCard
 *   plan={plan}
 *   billingInterval="monthly"
 *   isRecommended={plan.slug === 'pro'}
 *   loading={upgradeMutation.isPending}
 *   onUpgrade={(planId) => handleUpgrade(planId)}
 * />
 * ```
 */
export function PlanCard({
  plan,
  billingInterval,
  isRecommended = false,
  disabled = false,
  loading = false,
  onUpgrade,
  buttonText,
  className,
  showTrial = true,
  isCurrentPlan = false,
  isTrial = false,
  currentPlanPrice,
}: PlanCardProps) {
  const features = buildFeatureList(plan);
  const price =
    billingInterval === 'monthly'
      ? plan.monthly_price_cents
      : plan.yearly_price_cents;

  // Determine if this plan should be disabled
  // Current plan on active subscription cannot be selected
  // Current plan on trial CAN be selected (to convert trial to paid)
  const isDisabledCurrentPlan = isCurrentPlan && !isTrial;

  // Determine upgrade vs downgrade based on price comparison
  const isDowngrade = currentPlanPrice != null && price != null && price < currentPlanPrice;

  // Don't show recommended badge for downgrades
  const showRecommended = isRecommended && !isDowngrade;

  const getButtonText = (): string => {
    if (plan.is_contact_sales) return 'Contact Sales';
    if (isCurrentPlan && isTrial) return 'Start Subscription';
    if (isDisabledCurrentPlan) return 'Current Plan';
    return isDowngrade ? `Downgrade to ${plan.name}` : `Upgrade to ${plan.name}`;
  };

  const defaultButtonText = getButtonText();

  return (
    <Card
      className={cn(
        'relative flex flex-col justify-between gap-y-4 text-left',
        showRecommended &&
          'shadow-2xl ring-2 shadow-amber-500/70 ring-amber-500/50',
        className
      )}
      role="article"
      aria-label={`${plan.name} plan`}
    >
      {isCurrentPlan && (
        <span
          className="absolute -top-2.5 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-green-600 px-3 py-0.5 text-xs font-medium text-white"
          role="status"
          aria-label="Current plan"
        >
          <Check className="h-3 w-3" />
          Current Plan
        </span>
      )}
      {showRecommended && !isCurrentPlan && (
        <span
          className="absolute -top-2.5 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-amber-500 px-3 py-0.5 text-xs font-medium text-white"
          role="status"
          aria-label="Recommended plan"
        >
          <Star className="h-3 w-3 fill-current" />
          Recommended
        </span>
      )}
      <CardHeader className="text-center">
        <CardTitle>{plan.name}</CardTitle>
        <div className="flex justify-center">
          <span
            className="text-4xl font-semibold"
            aria-label={`Price: ${formatPrice(price, billingInterval)}`}
          >
            {formatPrice(price, billingInterval)}
          </span>
        </div>
        {plan.description && (
          <p className="text-muted-foreground line-clamp-2 min-h-10 text-sm">
            {plan.description}
          </p>
        )}
      </CardHeader>

      <CardContent className="">
        <Separator className="mb-4" />
        <ul className="space-y-3" role="list" aria-label="Plan features">
          {features.map((feature, index) => (
            <li
              key={index}
              className="flex items-center gap-2 text-sm"
              role="listitem"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{feature.text}</span>
            </li>
          ))}
        </ul>
        <Separator className="mt-4" />
      </CardContent>

      <CardFooter className="mt-auto flex-col gap-2">
        <Button
          className="w-full"
          onClick={() => {
            if (plan.is_contact_sales) {
              window.location.href = 'mailto:sales@loada.io?subject=Enterprise%20Plan%20Inquiry';
            } else {
              onUpgrade(plan.id);
            }
          }}
          disabled={disabled || loading || isDisabledCurrentPlan}
          variant={showRecommended ? 'default' : 'outline'}
          aria-label={buttonText || defaultButtonText}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            buttonText || defaultButtonText
          )}
        </Button>
        {showTrial && !plan.is_contact_sales && !isCurrentPlan && (
          <p className="text-muted-foreground h-4 text-xs">
            14 days free trial
          </p>
        )}
        {isCurrentPlan && isTrial && (
          <p className="text-muted-foreground h-4 text-xs">
            Convert your trial to a paid subscription
          </p>
        )}
      </CardFooter>
    </Card>
  );
}
